import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PetType } from '@spike/model';
import { fetchOrganizationServicesInfoThunk, MarketplaceStatus } from '@spike/marketplace-action';
import { OverFullWindow, Spinner } from 'components/UI';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';
import { useMasterData, useMarketplace, useApiClientWrapper } from 'hooks';
import {
    createEmptyDaycareService,
    Service,
    ServicePricing,
    FixedPriceOption,
    DAYCARE_DEFAULTS,
    DaycareDurationOption,
    LatePickupFeeType,
    MetadataDuration,
    DaycareMetadata,
    isMetadataDuration,
    DAYCARE_DURATION_OPTIONS,
    LATE_PICKUP_FEE_TYPES
} from 'model/Service';
import { timeStringToInteger, integerTimeToString } from 'utils/TimeOptionsUtils';
import { saveBulkServicesThunk } from 'actions/services/ServicesActions';
import { RootState } from 'store';
import styles from './DaycareForm.module.scss';
import { DaycarePetTypeStep } from './Steps/PetType/DaycarePetTypeStep';
import { DaycareDetailsStep } from './Steps/Details/DaycareDetailsStep';
import { DaycarePricingStep } from './Steps/Pricing/DaycarePricingStep';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import useNonInitialEffect from '@versiondos/hooks';
import { ServicesStatus } from 'reducers/services/ServicesState';

interface DaycareFormProps {
    mode: 'add' | 'edit';
    service?: Service;
    onClose: () => void;
}

export const DaycareForm: React.FC<DaycareFormProps> = props => {
    const dispatch = useDispatch();
    const masterData = useMasterData();
    const marketplace = useMarketplace();
    const apiClientWrapper = useApiClientWrapper();
    const fetchPendingRef = useRef(false);

    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    const [formStep, setFormStep] = useState(0);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingOrganizationServices, setIsLoadingOrganizationServices] = useState(true);
    const [isEditingSpecificStep, setIsEditingSpecificStep] = useState(false);

    const [daycareService, setDaycareService] = useState<Service>(() => {
        if (props.mode === 'edit' && props.service) {
            return props.service;
        }
        return createEmptyDaycareService(masterData, marketplace);
    });

    const [showExitPrompt, setShowExitPrompt] = useState(false);

    useEffect(() => {
        setIsLoadingOrganizationServices(true);
        fetchPendingRef.current = false;
        dispatch(fetchOrganizationServicesInfoThunk(apiClientWrapper));
        // Fetch once on mount; apiClientWrapper is recreated every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (marketplaceStatus === MarketplaceStatus.FetchingOrganizationServicesInfo) {
            fetchPendingRef.current = true;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.FetchOrganizationServicesInfoSuccess) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.Error) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }
    }, [marketplaceStatus]);

    useNonInitialEffect(() => {
        switch (servicesStatus) {
            case ServicesStatus.SaveSuccess:
            case ServicesStatus.SaveBulkSuccess:
                setIsLoading(false);
                setIsSuccess(true);
                break;
            case ServicesStatus.Error:
                setIsLoading(false);
                setIsSuccess(false);
                break;
        }
    }, [servicesStatus]);

    useEffect(() => {
        if (!daycareService.petTypes || !daycareService.pricing?.fixedPrice) return;

        const petTypeIds = daycareService.petTypes.map(pt => pt.id);
        const currentPricing = daycareService.pricing.fixedPrice;

        const missingPetTypes = daycareService.petTypes.filter(
            pt => !currentPricing.some(fp => fp.petType.id === pt.id)
        );

        const validPricing = currentPricing.filter(fp => petTypeIds.includes(fp.petType.id));

        if (missingPetTypes.length > 0 || validPricing.length !== currentPricing.length) {
            const newPricing: FixedPriceOption[] = [
                ...validPricing,
                ...missingPetTypes.map(petType => ({
                    petType,
                    price: undefined
                }))
            ];

            setDaycareService(prev => ({
                ...prev,
                pricing: {
                    ...prev.pricing!,
                    fixedPrice: newPricing
                }
            }));
        }
    }, [daycareService.petTypes]);

    const getTitle = () => {
        return props.mode === 'edit' ? `Editing ${daycareService.name}` : 'Adding a Daycare Service';
    };

    const getSuccessTitle = () => {
        return props.mode === 'edit' ? 'Updated Successfully' : 'Good Job';
    };

    const getSuccessMessage = () => {
        const action = props.mode === 'edit' ? 'updated' : 'added';
        return (
            <p className={styles.successDescription}>
                <b>{daycareService.name}</b> was {action} successfully.
            </p>
        );
    };

    const getSuccessButtonLabel = () => {
        return 'Go to Daycare Services';
    };

    const getCurrentDropOffTime = () => {
        if (props.mode === 'edit' && daycareService.dropOffTime) {
            return {
                from: integerTimeToString(daycareService.dropOffTime.start, '12h'),
                to: integerTimeToString(daycareService.dropOffTime.end, '12h')
            };
        }
        return DAYCARE_DEFAULTS.DROP_OFF_TIME;
    };

    const getCurrentPickUpTime = () => {
        if (props.mode === 'edit' && daycareService.pickUpTime) {
            return {
                from: integerTimeToString(daycareService.pickUpTime.start, '12h'),
                to: integerTimeToString(daycareService.pickUpTime.end, '12h')
            };
        }
        return DAYCARE_DEFAULTS.PICK_UP_TIME;
    };

    const nextStepHandler = () => setFormStep(formStep + 1);

    const editStepHandler = (step: number) => {
        setFormStep(step);
        setIsEditingSpecificStep(true);
    };

    const handlePetTypeStep = (petTypes: Array<PetType>, marketplaces: Service['marketplaces']) => {
        setDaycareService({
            ...daycareService,
            petTypes,
            marketplaces
        });
        nextStepHandler();
    };

    const handleDetailsStep = (details: {
        name: string;
        description: string;
        dropOffTime: { from: string; to: string };
        pickUpTime: { from: string; to: string };
        color?: string;
        onlineBooking: boolean;
        forms?: import('model/Forms').AssociatedForm[];
        roomsEnabled?: boolean;
        selectedRooms?: Array<string>;
        marketplaces: Service['marketplaces'];
    }) => {
        const existingDaycareData = daycareService.daycareData;
        const updatedMetadata = {
            duration_option: existingDaycareData?.durationOption || DAYCARE_DURATION_OPTIONS.FULL_DAY,
            duration_value: existingDaycareData?.durationValue || DAYCARE_DEFAULTS.DURATION,
            late_pickup_fee: {
                enabled: existingDaycareData?.latePickupFee?.enabled || false,
                type: existingDaycareData?.latePickupFee?.type || LATE_PICKUP_FEE_TYPES.PER_HOUR,
                grace_period: existingDaycareData?.latePickupFee?.gracePeriod || DAYCARE_DEFAULTS.GRACE_PERIOD,
                fees: existingDaycareData?.latePickupFee?.fees || {}
            },
            rooms_enabled: details.roomsEnabled || false,
            selected_rooms: details.selectedRooms || []
        };

        setDaycareService({
            ...daycareService,
            name: details.name,
            description: details.description,
            marketplaces: details.marketplaces,
            dropOffTime: {
                start: timeStringToInteger(details.dropOffTime.from),
                end: timeStringToInteger(details.dropOffTime.to)
            },
            pickUpTime: {
                start: timeStringToInteger(details.pickUpTime.from),
                end: timeStringToInteger(details.pickUpTime.to)
            },
            color: details.color || '',
            olbEnabled: details.onlineBooking,
            forms: details.forms || [],
            daycareData: {
                ...existingDaycareData,
                durationOption: existingDaycareData?.durationOption || DAYCARE_DURATION_OPTIONS.FULL_DAY,
                durationValue: existingDaycareData?.durationValue || DAYCARE_DEFAULTS.DURATION,
                latePickupFee: {
                    enabled: existingDaycareData?.latePickupFee?.enabled || false,
                    type: existingDaycareData?.latePickupFee?.type || LATE_PICKUP_FEE_TYPES.PER_HOUR,
                    gracePeriod: existingDaycareData?.latePickupFee?.gracePeriod || DAYCARE_DEFAULTS.GRACE_PERIOD,
                    fees: existingDaycareData?.latePickupFee?.fees || {}
                },
                roomsEnabled: details.roomsEnabled || false,
                selectedRooms: details.selectedRooms || []
            } as DaycareMetadata,
            duration: {
                metadata: JSON.stringify(updatedMetadata)
            } as MetadataDuration
        });
        nextStepHandler();
    };

    const handlePricingStep = async (
        pricing: ServicePricing,
        daycareData: {
            durationOption: DaycareDurationOption;
            durationValue: string;
            latePickupFee: {
                enabled: boolean;
                type?: LatePickupFeeType;
                gracePeriod: number;
                fees: Record<string, number>;
            };
            selectedRooms?: Array<string>;
        },
        marketplaces: Service['marketplaces']
    ) => {
        try {
            setIsLoading(true);
            let currentRoomsEnabled = false;
            let currentSelectedRooms: string[] = [];

            if (isMetadataDuration(daycareService.duration) && daycareService.duration.metadata) {
                try {
                    const currentMetadata = JSON.parse(daycareService.duration.metadata);
                    currentRoomsEnabled = currentMetadata.rooms_enabled || false;
                    currentSelectedRooms = currentMetadata.selected_rooms || [];
                } catch (error) {
                    console.warn('Error parsing current metadata:', error);
                }
            }

            const daycareMetadata = {
                duration_option: daycareData.durationOption,
                duration_value: daycareData.durationValue,
                late_pickup_fee: {
                    enabled: daycareData.latePickupFee.enabled,
                    type: daycareData.latePickupFee.type,
                    grace_period: daycareData.latePickupFee.gracePeriod,
                    fees: daycareData.latePickupFee.fees
                },
                rooms_enabled: currentRoomsEnabled,
                selected_rooms: currentSelectedRooms
            };

            const finalDaycareService: Service = {
                ...daycareService,
                marketplaces,
                pricing,
                duration: {
                    metadata: JSON.stringify(daycareMetadata)
                } as MetadataDuration
            };

            await dispatch(saveBulkServicesThunk(finalDaycareService));
        } catch (error) {
            // Show error notification
        } finally {
            setIsLoading(false);
        }
    };

    const getActiveStep = () => {
        if (props.mode === 'edit' && !isEditingSpecificStep) {
            return 3;
        }
        return formStep;
    };

    if (isSuccess) {
        return (
            <OverFullWindow
                showMobileLogo
                title={getTitle()}
                onClose={props.onClose}
            >
                <SuccessScreen
                    title={getSuccessTitle()}
                    message={getSuccessMessage()}
                    buttonLabel={getSuccessButtonLabel()}
                    onClick={props.onClose}
                />
            </OverFullWindow>
        );
    }

    return (
        <OverFullWindow
            showMobileLogo
            title={getTitle()}
            onClose={() => setShowExitPrompt(true)}
        >
            <>
                {showExitPrompt && (
                    <ExitWithoutSavingModal
                        onDiscard={() => props.onClose?.()}
                        onKeepEditing={() => setShowExitPrompt(false)}
                    />
                )}

                {isLoadingOrganizationServices ? (
                    <div className={styles.loadingContainer}>
                        <Spinner />
                    </div>
                ) : (
                    <div className={styles.container}>
                        <MultiStepForm
                            steps={[
                                {
                                    title: 'Pet Type and Locations',
                                    description: 'Select pet categories for this service',
                                    component: (
                                        <DaycarePetTypeStep
                                            selectedPetTypes={daycareService.petTypes}
                                            marketplaces={daycareService.marketplaces}
                                            serviceId={daycareService.id}
                                            onNext={handlePetTypeStep}
                                        />
                                    )
                                },
                                {
                                    title: 'Daycare Service Detail',
                                    component: (
                                        <DaycareDetailsStep
                                            formData={{
                                                name: daycareService.name,
                                                description: daycareService.description,
                                                dropOffTime: getCurrentDropOffTime(),
                                                pickUpTime: getCurrentPickUpTime(),
                                                color: daycareService.color || undefined,
                                                onlineBooking: daycareService.olbEnabled,
                                                forms: daycareService.forms || [],
                                                roomsEnabled: daycareService.daycareData?.roomsEnabled || false,
                                                selectedRooms: daycareService.daycareData?.selectedRooms || []
                                            }}
                                            petTypes={daycareService.petTypes}
                                            marketplaces={daycareService.marketplaces}
                                            serviceId={daycareService.id}
                                            onNext={handleDetailsStep}
                                        />
                                    )
                                },
                                {
                                    title: 'Pricing',
                                    description: 'Choose a pricing option for this daycare service',
                                    component: (
                                        <DaycarePricingStep
                                            selectedPetTypes={daycareService.petTypes}
                                            marketplaces={daycareService.marketplaces}
                                            existingService={props.mode === 'edit' ? props.service : undefined}
                                            onNext={handlePricingStep}
                                        />
                                    )
                                }
                            ]}
                            activeStep={getActiveStep()}
                            onEdit={editStepHandler}
                            flexibleNavigation={props.mode === 'edit'}
                        />
                    </div>
                )}
            </>
        </OverFullWindow>
    );
};

export default DaycareForm;
